import React, { useState, useMemo } from 'react';
import { SkillNode } from '../types';
import { X, Trophy, Lock, Zap, Loader2, CheckCircle2, CircleDashed, Star, Info, MousePointerClick } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface Props {
  data: SkillNode;
  onClose: () => void;
  onBridgeGap: (careerTitle: string, specificSkill: string) => void;
  onUpdateStatus?: (nodeId: string, status: 'locked' | 'unlocked' | 'mastered') => void;
  isLoadingResources: boolean;
}

export const SkillTreeVisualizer: React.FC<Props> = ({ data, onClose, onBridgeGap, onUpdateStatus, isLoadingResources }) => {
  const [selectedNode, setSelectedNode] = useState<SkillNode | null>(null);
  const { theme } = useTheme();

  const handleSearch = () => {
      if (selectedNode) {
          onBridgeGap(data.name, selectedNode.name);
      }
  };

  const handleStatusChange = (status: 'locked' | 'unlocked' | 'mastered') => {
      if (selectedNode && onUpdateStatus) {
          // Optimistic local update for the modal view
          setSelectedNode(prev => prev ? { ...prev, status } : null);
          onUpdateStatus(selectedNode.id, status);
      }
  };

  // Calculate Progress
  const stats = useMemo(() => {
      let total = 0;
      let mastered = 0;
      let unlocked = 0;

      const traverse = (node: SkillNode) => {
          total++;
          if (node.status === 'mastered') mastered++;
          if (node.status === 'unlocked') unlocked++;
          if (node.children) node.children.forEach(traverse);
      };
      traverse(data);
      return { total, mastered, unlocked, percent: Math.round((mastered / total) * 100) };
  }, [data]);

  // Recursive Tree Node Renderer
  const renderNode = (node: SkillNode, isRoot: boolean = false, index: number = 0, total: number = 1) => {
    const hasChildren = node.children && node.children.length > 0;
    
    // Connector logic
    const isFirst = index === 0;
    const isLast = index === total - 1;

    // Status Styling
    let statusColors = 'bg-slate-800 border-slate-600 text-slate-400'; // Locked
    
    if (node.status === 'mastered') {
        statusColors = 'bg-emerald-600 border-emerald-300 text-white shadow-[0_0_20px_rgba(16,185,129,0.4)]';
    } else if (node.status === 'unlocked') {
        statusColors = 'bg-amber-600 border-amber-300 text-white shadow-[0_0_15px_rgba(245,158,11,0.4)]';
    } else if (node.category === 'core') {
        statusColors = 'bg-indigo-600 border-indigo-300 text-white shadow-indigo-500/50';
    }

    return (
      <li key={node.id} className="relative flex flex-col items-center px-4">
        
        {/* Top Connectors (Skip for Root) */}
        {!isRoot && (
            <div className="absolute top-0 left-0 right-0 h-8 pointer-events-none">
                {/* Horizontal Line Left - Connects to previous sibling */}
                {!isFirst && (
                    <div className="absolute top-0 left-0 w-1/2 h-0.5 bg-slate-600/50"></div>
                )}
                
                {/* Horizontal Line Right - Connects to next sibling */}
                {!isLast && (
                    <div className="absolute top-0 right-0 w-1/2 h-0.5 bg-slate-600/50"></div>
                )}
                
                {/* Vertical Line - Connects to node below */}
                <div className={`absolute top-0 left-1/2 -translate-x-1/2 h-full w-0.5 ${node.status === 'mastered' ? 'bg-emerald-500/50' : 'bg-slate-600/50'}`}></div>
            </div>
        )}

        {/* Node Content */}
        <div className={`relative z-10 flex flex-col items-center ${!isRoot ? 'mt-8' : ''}`}>
            <button
                onClick={() => setSelectedNode(node)}
                className={`relative w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center border-4 transition-all duration-300 transform hover:scale-110 shadow-lg group ${statusColors}`}
            >
                {node.status === 'mastered' ? <CheckCircle2 size={28} /> : 
                 node.category === 'core' ? <Trophy size={24} /> : <Zap size={20} />}
                
                {/* Mini Status Badge */}
                {node.status !== 'mastered' && node.status !== 'unlocked' && (
                    <div className="absolute -bottom-1 -right-1 bg-slate-900 rounded-full p-1 border border-slate-700">
                        <Lock size={12} className="text-slate-500" />
                    </div>
                )}
                
                {/* Hover hint */}
                <div className="absolute inset-0 rounded-full bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <MousePointerClick size={20} className="text-white drop-shadow-md" />
                </div>
            </button>
            
            <div className="mt-2 text-center w-32">
                <span className={`inline-block text-[10px] md:text-xs font-bold px-2 py-1 rounded border break-words leading-tight transition-colors ${
                    node.status === 'mastered' ? 'bg-emerald-900/80 border-emerald-700 text-emerald-200' :
                    node.status === 'unlocked' ? 'bg-amber-900/80 border-amber-700 text-amber-200' :
                    'bg-slate-900/80 border-slate-700 text-slate-400'
                }`}>
                    {node.name}
                </span>
            </div>
        </div>

        {/* Bottom Connectors (Vertical line down to children container) */}
        {hasChildren && (
            <>
                <div className={`w-0.5 h-8 ${node.status === 'mastered' ? 'bg-emerald-500/50' : 'bg-slate-600/50'}`}></div>
                <ul className="flex flex-row justify-center relative">
                    {node.children!.map((child, idx) => 
                        renderNode(child, false, idx, node.children!.length)
                    )}
                </ul>
            </>
        )}
      </li>
    );
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in overflow-hidden ${theme.components.modal.overlay}`}>
        {/* Background Grid Effect - Always dark for contrast */}
        <div className="absolute inset-0 z-0 opacity-20 pointer-events-none" 
            style={{ 
                backgroundImage: 'linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px)', 
                backgroundSize: '40px 40px',
                backgroundColor: '#0f172a' // slate-950
            }}>
        </div>

      <div className="relative z-10 w-full h-full max-w-6xl flex flex-col">
        {/* Header with Progress */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 p-4 bg-slate-900/90 backdrop-blur-md rounded-2xl border border-slate-700 shrink-0 shadow-xl">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                Skill Tree: {data.name}
            </h2>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-3">
                {/* Instruction Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-xs font-medium">
                    <Info size={14} className="shrink-0" />
                    <span>Click any node to manage progress</span>
                </div>

                {/* Mastery Bar */}
                <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
                        <div className="h-full bg-emerald-500 transition-all duration-1000 ease-out" style={{ width: `${stats.percent}%` }}></div>
                    </div>
                    <span className="text-xs font-bold text-emerald-400 whitespace-nowrap">{stats.percent}% Mastered</span>
                </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 bg-slate-800 text-slate-400 hover:text-white rounded-full hover:bg-slate-700 transition-colors self-start md:self-center">
            <X size={24} />
          </button>
        </div>

        {/* Tree Container */}
        <div className="flex-1 overflow-auto custom-scrollbar flex justify-center items-start pt-10 pb-48">
          {/* New Flex Tree Structure */}
          <div className="min-w-max transform scale-90 md:scale-100 origin-top">
            <ul className="flex flex-row justify-center">
                {renderNode(data, true)}
            </ul>
          </div>
        </div>

        {/* Node Detail Modal (Pop-up inside) */}
        {selectedNode && (
            <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 w-full max-w-md p-6 rounded-2xl shadow-2xl animate-fade-in-up z-50 border border-slate-700 bg-slate-900/95 backdrop-blur-xl text-slate-100`}>
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            {selectedNode.status === 'mastered' && <CheckCircle2 size={20} className="text-emerald-500" />}
                            {selectedNode.name}
                        </h3>
                        <div className="flex gap-2 mt-1">
                            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                                selectedNode.category === 'hard' ? 'bg-blue-900/50 text-blue-300 border border-blue-800' :
                                selectedNode.category === 'soft' ? 'bg-purple-900/50 text-purple-300 border border-purple-800' : 'bg-indigo-900/50 text-indigo-300 border border-indigo-800'
                            }`}>
                                {selectedNode.category}
                            </span>
                        </div>
                    </div>
                    <button onClick={() => setSelectedNode(null)} className="text-slate-400 hover:text-white bg-slate-800 p-1 rounded-full"><X size={16}/></button>
                </div>
                
                <p className="text-sm leading-relaxed mb-6 text-slate-300">
                    {selectedNode.description}
                </p>

                {/* Status Controls */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                    <button 
                        onClick={() => handleStatusChange('locked')}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all ${
                            !selectedNode.status || selectedNode.status === 'locked' 
                            ? 'bg-slate-800 border-slate-600 text-white shadow-inner' 
                            : 'bg-transparent border-slate-800 text-slate-500 hover:bg-slate-800/50'
                        }`}
                    >
                        <Lock size={16} className="mb-1" />
                        <span className="text-[10px] font-bold uppercase">Locked</span>
                    </button>
                    <button 
                        onClick={() => handleStatusChange('unlocked')}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all ${
                            selectedNode.status === 'unlocked' 
                            ? 'bg-amber-900/40 border-amber-600 text-amber-300 shadow-inner' 
                            : 'bg-transparent border-slate-800 text-slate-500 hover:bg-amber-900/20 hover:text-amber-400'
                        }`}
                    >
                        <CircleDashed size={16} className="mb-1" />
                        <span className="text-[10px] font-bold uppercase">Learning</span>
                    </button>
                    <button 
                        onClick={() => handleStatusChange('mastered')}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all ${
                            selectedNode.status === 'mastered' 
                            ? 'bg-emerald-900/40 border-emerald-600 text-emerald-300 shadow-inner' 
                            : 'bg-transparent border-slate-800 text-slate-500 hover:bg-emerald-900/20 hover:text-emerald-400'
                        }`}
                    >
                        <Star size={16} className="mb-1" />
                        <span className="text-[10px] font-bold uppercase">Mastered</span>
                    </button>
                </div>

                <div className="pt-4 border-t border-slate-800">
                    <button 
                        onClick={handleSearch}
                        disabled={isLoadingResources}
                        className="w-full py-3 font-bold text-sm shadow-lg transition-colors flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoadingResources ? <Loader2 size={16} className="animate-spin" /> : <Loader2 size={16} className="opacity-0 hidden" />} 
                        {isLoadingResources ? "Searching..." : "Find Learning Resources"}
                    </button>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};